import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import CallToAction from '../../components/CallToAction';
import './ProjectPages.css';

// Import images
import aiHeroImage from '../../assets/images/ai.jpg';

const DataScience = () => {
  const faqData = [
    {
      question: "Veri bilimi ve yapay zeka çözümleri ne işe yarar?",
      answer: "İşletmenizin verilerini analiz ederek anlamlı içgörüler çıkarıyoruz. Geleceği öngören tahminler yapıyor, otomatik kararlar alıyor ve süreçleri optimize ediyoruz. Böylece işletmeler daha iyi kararlar alır, maliyetlerini düşürür ve rekabet gücünü artırır."
    },
    {
      question: "Hangi sektörler için veri bilimi çözümleri sunuyorsunuz?",
      answer: "Eğitim, sağlık, finans, perakende, üretim gibi birçok sektör için veri bilimi ve yapay zeka çözümleri sunuyoruz. Her sektörün kendine özgü ihtiyaçlarına göre özelleştirilmiş çözümler geliştiriyoruz."
    },
    {
      question: "Veri analizi süreci nasıl işliyor?",
      answer: "İşletmenizin verilerini topluyor, temizliyor ve analiz ediyoruz. Anlaşılır raporlar ve görsel dashboard'lar hazırlıyoruz. Gelecekte ne olabileceğini tahmin ediyor, öneriler sunuyoruz."
    },
    {
      question: "Yapay zeka çözümleri maliyetli mi?",
      answer: "Çözümlerimiz işletmenizin ihtiyaçlarına göre özelleştirilir. Farklı bütçe seçenekleri sunuyoruz. Detaylı bilgi ve özel teklifler için bizimle iletişime geçebilirsiniz."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Data Science and AI Solutions",
    "provider": {
      "@type": "Organization",
      "name": "ProgenX"
    },
    "description": "Veri bilimi ve yapay zeka çözümleri ile işletmenizin verilerini değere dönüştürün.",
    "areaServed": "TR"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="project-page data-science">
      <Helmet>
        <title>Veri Bilimi ve Yapay Zeka Çözümleri | İşletmenizi Geleceğe Taşıyın - ProgenX 2024</title>
        <meta name="description" content="ProgenX veri bilimi ve yapay zeka çözümleri ile işletmenizin verilerini anlamlı içgörülere dönüştürün. Geleceği öngören tahminler, otomatik kararlar, süreç optimizasyonu. Türkiye'nin önde gelen veri bilimi şirketi." />
        <meta name="keywords" content="veri bilimi, yapay zeka çözümleri, makine öğrenmesi, derin öğrenme, büyük veri analizi, veri madenciliği, iş zekası, prediktif analitik, türkiye veri bilimi, ai danışmanlık" />
        <meta property="og:title" content="Veri Bilimi ve Yapay Zeka Çözümleri | ProgenX" />
        <meta property="og:description" content="İşletmenizin verilerini değere dönüştüren yapay zeka ve veri bilimi çözümleri. Daha iyi kararlar, daha düşük maliyetler." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/projeler/veri-bilimi-yapay-zeka" />
        <meta property="og:image" content="https://progenxacademy.com/ai.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://progenxacademy.com/projeler/veri-bilimi-yapay-zeka" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <Hero 
        title="Veri Bilimi ve Yapay Zeka" 
        backgroundImage={aiHeroImage}
      />

      <div className="project-content">
        <div className="container">
          <section className="project-overview">
            <h2>Verilerinizden Değer Yaratıyoruz</h2>
            <p>
              İşletmenizin elindeki veriler, aslında büyük bir hazine. Ancak bu hazineyi doğru şekilde 
              işlemezseniz değer yaratamazsınız. ProgenX olarak, yapay zeka ve veri bilimi çözümlerimizle 
              verilerinizden anlamlı içgörüler çıkarıyor, kararlarınızı veriye dayalı hale getiriyor ve 
              işletmenizin rekabet gücünü artırıyoruz.
            </p>
          </section>

          <section className="features">
            <h2>İşletmenize Nasıl Değer Katıyoruz?</h2>
            <div className="features-grid">
              <div className="feature-item">
                <h3>Verilerinizi Anlamlı Bilgilere Dönüştürüyoruz</h3>
                <p>
                  İşletmenizin tüm verilerini toplayıp analiz ediyor, anlaşılır raporlar ve 
                  görsel dashboard'lar halinde sunuyoruz. Böylece verilerinizden ne anlamanız 
                  gerektiğini net bir şekilde görürsünüz.
                </p>
              </div>

              <div className="feature-item">
                <h3>Geleceği Öngören Tahminler Yapıyoruz</h3>
                <p>
                  Geçmiş verilerinizi analiz ederek gelecekte ne olabileceğini tahmin ediyoruz. 
                  Müşteri davranışları, satış trendleri, olası sorunlar - hepsini önceden 
                  görmenize yardımcı oluyoruz.
                </p>
              </div>

              <div className="feature-item">
                <h3>Otomatik Kararlar Alıyoruz</h3>
                <p>
                  Görüntü, ses, metin gibi farklı veri türlerini işleyerek otomatik kararlar 
                  almanızı sağlıyoruz. Müşteri yorumlarını analiz ediyor, görselleri tanıyor, 
                  içerik üretiyoruz.
                </p>
              </div>

              <div className="feature-item">
                <h3>Süreçlerinizi Optimize Ediyoruz</h3>
                <p>
                  İş süreçlerinizi analiz ederek nerede verimlilik kaybı olduğunu buluyor, 
                  kaynaklarınızı en iyi şekilde kullanmanızı sağlıyoruz. Riskleri önceden 
                  tespit ediyor, maliyetleri düşürüyoruz.
                </p>
              </div>
            </div>
          </section>

          <section className="benefits">
            <h2>İşletmeniz İçin Ne Sağlıyoruz?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Daha İyi Kararlar Alın</h3>
                <p>Sezgileriniz yerine verileriniz konuşsun. Veriye dayalı kararlar alarak 
                başarı şansınızı artırın ve hataları azaltın.</p>
              </div>

              <div className="benefit-item">
                <h3>Daha Verimli Çalışın</h3>
                <p>Süreçlerinizi optimize ederek aynı işi daha az kaynakla yapın. 
                Zaman ve enerji tasarrufu sağlayarak büyümenize odaklanın.</p>
              </div>

              <div className="benefit-item">
                <h3>Rakip Önüne Geçin</h3>
                <p>Yapay zeka destekli çözümlerle rakiplerinizden bir adım önde olun. 
                Müşterilerinizi daha iyi anlayın, onlara daha iyi hizmet verin.</p>
              </div>

              <div className="benefit-item">
                <h3>Maliyetlerinizi Düşürün</h3>
                <p>Akıllı otomasyon ve optimizasyon ile gereksiz harcamaları azaltın. 
                Kaynaklarınızı en verimli şekilde kullanarak kârlılığınızı artırın.</p>
              </div>
            </div>
          </section>

          <section className="faq-section">
            <h2>Sıkça Sorulan Sorular</h2>
            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <CallToAction 
        title="Verilerinizden Değer Yaratmaya Başlayın"
        subtitle="İşletmenizin verilerini analiz edip, kararlarınızı veriye dayalı hale getirmek için bizimle iletişime geçin."
        buttonText="Bizimle İletişime Geçin"
        buttonLink="/iletisim"
      />
    </div>
  );
};

export default DataScience; 